import React from "react";
import { Link } from "react-router-dom";
import {
  Phone, MapPin, MessageCircle, Store, HelpCircle, FileQuestion, Handshake,
  Heart, ShieldCheck, Sparkles, Gift, CalendarDays, ArrowRight,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useStore } from "../context/StoreContext";
import { waLink, WhatsAppIcon } from "../components/EnquiryWidget";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";

const NAVY = "#1B1F5C";
const STORE_IMG = "https://customer-assets-v7afamib.emergentagent.net/job_appa-playtime/artifacts/4h2c77vd_03.png";
const ADDRESS = "Knowledge City, 12th Floor, Dallas Center, Hitech City Main Rd, Silpa Gram Craft Village, Madhapur, Rai Durg, Hyderabad, Telangana 500032";

const Shell = ({ title, accent, sub, children, testId }) => {
  const { wishlist, cart } = useStore();
  return (
    <div className="bg-white min-h-screen">
      <Header cartCount={cart.length} wishlistCount={wishlist.length} />
      <main data-testid={testId}>
        <div className="max-w-7xl mx-auto px-4 pt-4 text-left">
          <p className="text-[12px] font-bold" style={{ color: NAVY }}>
            <Link to="/" className="hover:text-[#35C3C1] transition-colors">Home</Link>
            <span className="mx-2 text-gray-400">&gt;</span>
            <span className="text-gray-500">{title} {accent}</span>
          </p>
        </div>
        <section className="max-w-7xl mx-auto px-4 mt-4">
          <div className="rounded-3xl px-6 md:px-10 py-8 md:py-10 relative overflow-hidden text-left" style={{ backgroundColor: "#FBF3E4" }}>
            <svg className="absolute top-5 right-8 w-6 h-6 text-[#F79433]" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z"/></svg>
            <h1 className="font-display font-extrabold text-3xl md:text-4xl" style={{ color: NAVY }}>
              {title} <span className="text-[#35C3C1]">{accent}</span>
            </h1>
            {sub && <p className="mt-2 text-[13.5px] font-semibold text-[#5A5A75] max-w-2xl leading-relaxed">{sub}</p>}
          </div>
        </section>
        {children}
      </main>
      <Footer />
    </div>
  );
};

/* ---------------- Our Story ---------------- */
export function OurStory() {
  const values = [
    { icon: Heart, title: "Made with Love", desc: "Every toy in our catalog is hand-picked with the same care we'd take for our own children." },
    { icon: ShieldCheck, title: "Safety First", desc: "Child-safe, non-toxic and age-appropriate — we never compromise on what reaches little hands." },
    { icon: Sparkles, title: "Joy of Discovery", desc: "From first rattles to robotics kits, we believe play is how children discover the world." },
    { icon: Handshake, title: "Growing Together", desc: "As an EBG Group venture, we're building stores, partnerships and childhoods that last." },
  ];
  return (
    <Shell title="Our" accent="Story" sub="The tale of a little girl, her Appa, and a whole world of toys." testId="our-story-page">
      <section className="max-w-7xl mx-auto px-4 mt-8 grid lg:grid-cols-2 gap-8 items-center">
        <div className="rounded-3xl overflow-hidden shadow-lg">
          <img src={STORE_IMG} alt="Inside an Adhira & Appa Toys store" className="w-full h-[300px] md:h-[380px] object-cover" />
        </div>
        <div className="text-left space-y-4">
          <h2 className="font-display font-extrabold text-2xl" style={{ color: NAVY }}>Why "Adhira & Appa"?</h2>
          <p className="text-[13.5px] font-semibold text-gray-500 leading-relaxed">
            Our name celebrates the most magical shopping duo we know — a curious little one (Adhira) and her doting father (Appa).
            That giggle when a child finds the perfect toy, and the quiet smile on a parent's face — that's the moment we built this brand around.
          </p>
          <p className="text-[13.5px] font-semibold text-gray-500 leading-relaxed">
            Born in Hyderabad as a venture of the EBG Group, Adhira & Appa Toys is a young brand with a big dream:
            to become India's most loved toy destination. We're starting with a thoughtfully curated catalog spanning every age —
            from a baby's first rattle to a teen's first robotics kit — and our first physical stores are on the way.
          </p>
          <p className="text-[13.5px] font-semibold text-gray-500 leading-relaxed">
            We're just getting started, and that's the exciting part. Every enquiry, every visit and every franchise partner
            becomes part of our founding story.
          </p>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-4 mt-10 mb-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {values.map((v) => (
          <div key={v.title} className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 text-left hover:-translate-y-1 transition-transform duration-200">
            <span className="h-11 w-11 rounded-2xl bg-[#E7F8F8] flex items-center justify-center">
              <v.icon size={20} className="text-[#35C3C1]" />
            </span>
            <p className="mt-3 font-display font-extrabold text-[15px]" style={{ color: NAVY }}>{v.title}</p>
            <p className="mt-1.5 text-[12.5px] font-semibold text-gray-500 leading-relaxed">{v.desc}</p>
          </div>
        ))}
      </section>
    </Shell>
  );
}

/* ---------------- Blog ---------------- */
const BLOG_IMG = "https://static.prod-images.emergentagent.com/jobs/63357c08-e046-4224-b5c1-355c0d6a5775/images/{}.jpeg";
const posts = [
  {
    title: "5 Toys That Boost Early Brain Development",
    date: "June 2026", tag: "Parenting",
    image: BLOG_IMG.replace("{}", "dbd343d3c0b4ff7017554549ba671dab5eaf95b512a2766237c0a06ea3019a0a"),
    body: "Stacking blocks, shape sorters, sensory balls, activity mats and simple musical toys do more than entertain — they wire young brains for problem-solving, motor control and language. The rule of thumb: the simpler the toy, the harder the brain works. Rotate a small set of open-ended toys instead of overwhelming your little one with too many at once.",
  },
  {
    title: "How to Pick the Perfect Toy for Every Age",
    date: "June 2026", tag: "Buying Guide",
    image: BLOG_IMG.replace("{}", "1740ad4bf5343268f845e42d8fbc754bcd8ce9d94eb9af6a7a8b0ceff29aeb75"),
    body: "Age labels exist for a reason — they reflect both safety and developmental stage. Under 1, look for texture and sound. Toddlers thrive on push-pull and stacking play. Ages 3-6 love pretend play and building. School-age kids are ready for STEM kits, sports and strategy games. When in doubt, browse our Shop by Age section or simply send us an enquiry — we love matchmaking kids and toys.",
  },
  {
    title: "Screen-Free Weekends: Play Ideas the Whole Family Will Love",
    date: "May 2026", tag: "Family Time",
    image: BLOG_IMG.replace("{}", "7ca9779b71d53367254ba245ae64dea29aef924c9d51098d1884e111e211bd3d"),
    body: "Board game tournaments, backyard cricket, tent-house picnics in the living room and 1000-piece puzzle marathons — a screen-free weekend doesn't need elaborate planning, just the right props. Keep a 'family game shelf' at kid height so the invitation to play is always open.",
  },
  {
    title: "Why STEM Toys Matter More Than Ever",
    date: "May 2026", tag: "Learning",
    image: BLOG_IMG.replace("{}", "2ef87945be53d31fcd976c5020bc6d829b85426e16a8791ff758da03fb0665e8"),
    body: "Gears, circuits, robots and science kits teach children that it's okay to fail, tinker and try again — the exact mindset tomorrow's world rewards. Start small with a gears kit around age 6, graduate to science labs by 8, and robotics kits by 11. The best part? STEM play is often the most fun a parent and child can have together.",
  },
];

export function Blog() {
  return (
    <Shell title="Our" accent="Blog" sub="Play ideas, buying guides and parenting reads from Team Adhira & Appa." testId="blog-page">
      <section className="max-w-7xl mx-auto px-4 mt-8 mb-4 grid sm:grid-cols-2 gap-6">
        {posts.map((p) => (
          <article key={p.title} className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden text-left hover:shadow-lg transition-shadow" data-testid="blog-post-card">
            <div className="bg-[#F7F7FA] h-44 flex items-center justify-center">
              <img src={p.image} alt={p.title} className="h-full w-full object-contain mix-blend-multiply p-4" loading="lazy" />
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 text-[11px] font-extrabold">
                <span className="px-2.5 py-0.5 rounded-full bg-[#E7F8F8] text-[#35C3C1]">{p.tag}</span>
                <span className="flex items-center gap-1 text-gray-400"><CalendarDays size={12} /> {p.date}</span>
              </div>
              <h2 className="mt-2.5 font-display font-extrabold text-[17px] leading-snug" style={{ color: NAVY }}>{p.title}</h2>
              <p className="mt-2 text-[12.5px] font-semibold text-gray-500 leading-relaxed">{p.body}</p>
            </div>
          </article>
        ))}
      </section>
    </Shell>
  );
}

/* ---------------- FAQs ---------------- */
const faqs = [
  { q: "How do I buy a toy from Adhira & Appa Toys?", a: "We currently work on an enquiry model. Browse the catalog, then tap any \"Enquire Now\" button or the WhatsApp bubble — share the toy you love and our team will confirm availability, pricing and the easiest way to get it to you." },
  { q: "Why don't I see prices on the website?", a: "As a young brand preparing to open our first stores, our range and offers are updated frequently. Enquire about any toy and we'll share the latest price and current offers right away." },
  { q: "Are your toys safe for children?", a: "Absolutely. We only curate child-safe, non-toxic toys from trusted manufacturers, and every listing shows a recommended age range so you can choose confidently." },
  { q: "Do you have a physical store I can visit?", a: "Our first stores are opening soon! Until then, our full catalog lives here on the website and our team is one WhatsApp message away. Follow us on Instagram for store-launch announcements." },
  { q: "Can you help me choose a gift?", a: "That's our favourite job. Use the Gift Finder on the Gifts page, browse Shop by Age, or simply tell us the child's age and interests in an enquiry — we'll suggest perfect options." },
  { q: "How does the franchise process work?", a: "Submit the franchise form on the Our Franchise page. We respond within 24 hours, then walk you through discussion, site evaluation, agreement, store setup and launch — end to end." },
  { q: "What store formats do you offer franchise partners?", a: "Three formats: Kiosk (150–300 sq.ft.), Flagship Store – Mall (800–1500 sq.ft.) and Signature Store (1500–3000 sq.ft.). Early partners get founding-partner benefits and prime territories." },
  { q: "How quickly do you respond to enquiries?", a: "Within 24 hours on working days — usually much faster on WhatsApp during business hours." },
];

export function FAQs() {
  return (
    <Shell title="Frequently Asked" accent="Questions" sub="Quick answers about enquiries, toys, safety and our franchise." testId="faqs-page">
      <section className="max-w-3xl mx-auto px-4 mt-8 mb-6 text-left">
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 data-[state=open]:border-[#35C3C1]/50">
              <AccordionTrigger className="text-[13.5px] font-extrabold hover:no-underline" style={{ color: NAVY }} data-testid={`faq-trigger-${i}`}>
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-[12.5px] font-semibold text-gray-500 leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="mt-8 rounded-3xl p-6 text-center" style={{ backgroundColor: "#FBF3E4" }}>
          <p className="font-display font-extrabold text-lg" style={{ color: NAVY }}>Still have a question?</p>
          <div className="mt-3 flex items-center justify-center gap-3 flex-wrap">
            <button onClick={() => window.dispatchEvent(new CustomEvent("open-enquiry"))} data-testid="faq-enquire-btn" className="px-6 h-11 rounded-full text-white text-[11.5px] font-extrabold tracking-widest" style={{ backgroundColor: NAVY }}>SEND AN ENQUIRY</button>
            <a href={waLink("Hi! I have a question about Adhira & Appa Toys.")} target="_blank" rel="noopener noreferrer" className="px-6 h-11 rounded-full bg-[#25D366] text-white text-[11.5px] font-extrabold tracking-widest inline-flex items-center gap-2"><WhatsAppIcon size={16} /> WHATSAPP US</a>
          </div>
        </div>
      </section>
    </Shell>
  );
}

/* ---------------- Contact Us ---------------- */
export function ContactUs() {
  return (
    <Shell title="Contact" accent="Us" sub="We'd love to hear from you — questions, gift help or franchise talk." testId="contact-page">
      <section className="max-w-7xl mx-auto px-4 mt-8 mb-4 grid md:grid-cols-3 gap-5">
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-7 text-left">
          <span className="h-11 w-11 rounded-2xl bg-[#E7F8F8] flex items-center justify-center"><Phone size={20} className="text-[#35C3C1]" /></span>
          <p className="mt-3 font-display font-extrabold text-[16px]" style={{ color: NAVY }}>Call or WhatsApp</p>
          <a href="tel:+918828131962" className="mt-1.5 block text-[14px] font-extrabold text-[#35C3C1]" data-testid="contact-phone-link">+91 88281 31962</a>
          <p className="mt-1 text-[12px] font-semibold text-gray-500">Mon – Sat, 10 AM – 7 PM</p>
          <a href={waLink("Hi! I'd like to get in touch with Adhira & Appa Toys.")} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 px-5 h-10 rounded-full bg-[#25D366] text-white text-[11px] font-extrabold tracking-widest"><WhatsAppIcon size={15} /> CHAT NOW</a>
        </div>
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-7 text-left">
          <span className="h-11 w-11 rounded-2xl bg-[#FDEEDC] flex items-center justify-center"><MapPin size={20} className="text-[#F79433]" /></span>
          <p className="mt-3 font-display font-extrabold text-[16px]" style={{ color: NAVY }}>Our Office</p>
          <p className="mt-1.5 text-[12.5px] font-semibold text-gray-500 leading-relaxed">{ADDRESS}</p>
        </div>
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-7 text-left">
          <span className="h-11 w-11 rounded-2xl bg-[#FDE7EF] flex items-center justify-center"><MessageCircle size={20} className="text-[#F0426A]" /></span>
          <p className="mt-3 font-display font-extrabold text-[16px]" style={{ color: NAVY }}>Send an Enquiry</p>
          <p className="mt-1.5 text-[12.5px] font-semibold text-gray-500 leading-relaxed">Product questions, gift help or anything else — drop us a note and we'll reply within 24 hours.</p>
          <button onClick={() => window.dispatchEvent(new CustomEvent("open-enquiry"))} data-testid="contact-enquire-btn" className="mt-4 px-5 h-10 rounded-full text-white text-[11px] font-extrabold tracking-widest" style={{ backgroundColor: NAVY }}>ENQUIRE NOW</button>
        </div>
      </section>
    </Shell>
  );
}

/* ---------------- Store Locator ---------------- */
export function StoreLocator() {
  return (
    <Shell title="Store" accent="Locator" sub="Our first stores are opening soon — here's where to find us today." testId="store-locator-page">
      <section className="max-w-7xl mx-auto px-4 mt-8 mb-4 grid lg:grid-cols-2 gap-8 items-center">
        <div className="rounded-3xl overflow-hidden shadow-lg relative">
          <img src={STORE_IMG} alt="Adhira & Appa Toys store" className="w-full h-[300px] md:h-[360px] object-cover" />
          <span className="absolute top-4 left-4 px-3.5 py-1.5 rounded-full bg-[#FCD44E] text-[11px] font-extrabold tracking-widest" style={{ color: NAVY }}>OPENING SOON</span>
        </div>
        <div className="text-left">
          <h2 className="font-display font-extrabold text-2xl" style={{ color: NAVY }}>Physical stores, coming your way</h2>
          <p className="mt-3 text-[13.5px] font-semibold text-gray-500 leading-relaxed">
            We're a young brand building our first Adhira & Appa Toys stores — kiosks, mall stores and signature experience
            stores. Follow our socials for launch announcements, and if you'd like to bring a store to your city, our
            franchise team would love to talk.
          </p>
          <div className="mt-5 bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <p className="text-[12px] font-extrabold text-[#35C3C1] tracking-widest">HEAD OFFICE — HYDERABAD</p>
            <p className="mt-2 flex items-start gap-2 text-[12.5px] font-semibold text-gray-500 leading-relaxed">
              <MapPin size={15} className="text-[#F79433] shrink-0 mt-0.5" /> {ADDRESS}
            </p>
            <a href="tel:+918828131962" className="mt-2 flex items-center gap-2 text-[13px] font-extrabold" style={{ color: NAVY }}>
              <Phone size={14} className="text-[#35C3C1]" /> +91 88281 31962
            </a>
          </div>
          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <Link to="/franchise" data-testid="locator-franchise-cta" className="px-6 h-11 rounded-full text-white text-[11.5px] font-extrabold tracking-widest inline-flex items-center gap-2" style={{ backgroundColor: NAVY }}>
              OPEN A STORE IN YOUR CITY <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </Shell>
  );
}

/* ---------------- Help Center ---------------- */
export function HelpCenter() {
  const tiles = [
    { icon: MessageCircle, title: "Product Enquiries", desc: "Ask about any toy — availability, details and offers.", action: () => window.dispatchEvent(new CustomEvent("open-enquiry")), label: "Enquire Now" },
    { icon: FileQuestion, title: "FAQs", desc: "Quick answers to the questions we hear most.", to: "/faqs", label: "Read FAQs" },
    { icon: Phone, title: "Talk to Us", desc: "Call or WhatsApp us Mon–Sat, 10 AM – 7 PM.", to: "/contact-us", label: "Contact Us" },
    { icon: Store, title: "Visit a Store", desc: "Our first stores are opening soon — see where.", to: "/store-locator", label: "Store Locator" },
    { icon: Handshake, title: "Franchise Support", desc: "Everything about partnering with us.", to: "/franchise", label: "Our Franchise" },
    { icon: Gift, title: "Gift Help", desc: "Not sure what to gift? Let us play matchmaker.", to: "/gifts", label: "Explore Gifts" },
  ];
  return (
    <Shell title="Help" accent="Center" sub="Everything you need, one tap away." testId="help-center-page">
      <section className="max-w-7xl mx-auto px-4 mt-8 mb-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {tiles.map((t) => (
          <div key={t.title} className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 text-left flex flex-col hover:-translate-y-1 transition-transform duration-200">
            <span className="h-11 w-11 rounded-2xl bg-[#E7F8F8] flex items-center justify-center"><t.icon size={20} className="text-[#35C3C1]" /></span>
            <p className="mt-3 font-display font-extrabold text-[15.5px]" style={{ color: NAVY }}>{t.title}</p>
            <p className="mt-1.5 text-[12.5px] font-semibold text-gray-500 leading-relaxed flex-1">{t.desc}</p>
            {t.to ? (
              <Link to={t.to} className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-extrabold text-[#35C3C1]">{t.label} <ArrowRight size={13} /></Link>
            ) : (
              <button onClick={t.action} className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-extrabold text-[#35C3C1]" data-testid="help-enquire-btn">{t.label} <ArrowRight size={13} /></button>
            )}
          </div>
        ))}
      </section>
    </Shell>
  );
}

/* ---------------- Privacy Policy ---------------- */
const LegalBlock = ({ heading, children }) => (
  <div className="mt-6">
    <h2 className="font-display font-extrabold text-[16px]" style={{ color: NAVY }}>{heading}</h2>
    <p className="mt-1.5 text-[12.5px] font-semibold text-gray-500 leading-relaxed">{children}</p>
  </div>
);

export function PrivacyPolicy() {
  return (
    <Shell title="Privacy" accent="Policy" sub="How we handle your information — plainly and honestly." testId="privacy-page">
      <section className="max-w-3xl mx-auto px-4 mt-4 mb-8 text-left">
        <LegalBlock heading="What we collect">
          When you send an enquiry (general or franchise) or subscribe to our newsletter, we collect the details you share — such as your name, phone number, email, city and message. We don't run user accounts, carts or payments on this website.
        </LegalBlock>
        <LegalBlock heading="How we use it">
          Your details are used solely to respond to your enquiry, help you find the right toys, or discuss franchise opportunities. We may contact you by phone, WhatsApp or email regarding your request.
        </LegalBlock>
        <LegalBlock heading="What we never do">
          We never sell or rent your personal information to third parties. Your data is stored securely and accessed only by the Adhira & Appa Toys team.
        </LegalBlock>
        <LegalBlock heading="Third-party links">
          Our website links to platforms like WhatsApp, Instagram, Facebook, YouTube, LinkedIn and X. Their privacy practices are governed by their own policies.
        </LegalBlock>
        <LegalBlock heading="Your choices">
          Want your enquiry data removed? Just tell us at +91 88281 31962 or via any enquiry channel and we'll delete it promptly.
        </LegalBlock>
        <LegalBlock heading="Contact">
          Adhira & Appa Toys (an EBG Group venture), {ADDRESS}. Phone: +91 88281 31962.
        </LegalBlock>
      </section>
    </Shell>
  );
}

/* ---------------- Terms & Conditions ---------------- */
export function Terms() {
  return (
    <Shell title="Terms &" accent="Conditions" sub="The simple ground rules for using our website." testId="terms-page">
      <section className="max-w-3xl mx-auto px-4 mt-4 mb-8 text-left">
        <LegalBlock heading="About this website">
          This website is a product catalog and enquiry platform operated by Adhira & Appa Toys, an EBG Group venture based in Hyderabad, India. It does not process online orders or payments.
        </LegalBlock>
        <LegalBlock heading="Catalog information">
          We work hard to keep product details, images and availability accurate, but our catalog is refreshed frequently as a growing brand. Final availability and pricing are confirmed when you enquire.
        </LegalBlock>
        <LegalBlock heading="Enquiries">
          By submitting an enquiry you consent to being contacted by our team regarding your request. Please provide accurate contact details so we can reach you.
        </LegalBlock>
        <LegalBlock heading="Franchise information">
          Franchise details on this website (store formats, sizes, processes) are indicative and shared for initial discussion. Final terms are defined only in a signed franchise agreement.
        </LegalBlock>
        <LegalBlock heading="Intellectual property">
          The Adhira & Appa Toys name, logo and website content belong to us or our licensors. Product brand names shown belong to their respective owners.
        </LegalBlock>
        <LegalBlock heading="Questions">
          Reach us anytime at +91 88281 31962 or through the Contact Us page.
        </LegalBlock>
      </section>
    </Shell>
  );
}
